import React, { useState, useCallback, useContext, useEffect } from 'react';

import { Input, Button, UploadArea, Notification, Spinner, Label } from '@ergeon/core-components';
import { EnvelopeOpenIcon, UserPlusIcon } from '@ergeon/icons/hero/micro';

import { AgentContext } from '../../../../../context-providers/AgentContext';
import { UnifiedDialogEntry } from '../../../../../types';
import { createOptimisticEmail } from '../../utils/createOptimisticEntry';

import EmailAutocompleteInput from './components/EmailAutocompleteInput';
import RichTextInput from './components/RichTextInput';
import { htmlToMarkdown } from './components/RichTextInput/utils/converters/htmlToMarkdown';
import SenderSelector from './components/SenderSelector';
import { MAX_ATTACHMENTS, MAX_ATTACHMENT_SIZE, SHARED_SENDER_EMAILS, DEFAULT_SENDER_EMAIL, DEFAULT_DISPLAY_NAME } from './constants';
import { useCreateEmail } from './hooks/useCreateEmail';
import { useComposeDraft, serializeAttachments, deserializeAttachments } from './hooks/useComposeDraft';
import { useFileUploadPaste } from './hooks/useFileUploadPaste';
import type { Attachment, EmailDraft, SerializableAttachment } from './types';
import { parseEmailsFromString } from './utils/emailRecipients';
import { htmlToPlainText } from './utils/htmlUtils';

const LABEL_SUBJECT = 'Subject';
const LABEL_MESSAGE = 'Body';
const PLACEHOLDER_SUBJECT = 'Enter email subject...';
const PLACEHOLDER_MESSAGE = 'Type your email message...';
const BUTTON_TEXT = 'Send Email';
const BUTTON_LOADING_TEXT = 'Sending...';

type EmailInputProps = {
  customerId?: number;
  customerName?: string;
  customerEmail?: string;
  onSuccess?: () => void;
  initialSubject?: string | null;
  onAddOptimisticMessage?: (entry: UnifiedDialogEntry) => void;
  onUpdateOptimisticMessage?: (optimisticId: string, updates: Partial<UnifiedDialogEntry>) => void;
  onRemoveOptimisticMessage?: (optimisticId: string) => void;
};

const EmailInput: React.FC<EmailInputProps> = ({
  customerId,
  customerName,
  customerEmail,
  onSuccess,
  initialSubject,
  onAddOptimisticMessage,
  onUpdateOptimisticMessage: _onUpdateOptimisticMessage,
  onRemoveOptimisticMessage,
}) => {
  const { fullName: agentName, identity: agentEmail } = useContext(AgentContext);

  const [subject, setSubject] = useState(initialSubject || '');
  const [body, setBody] = useState(''); // Body is now HTML (WYSIWYG)
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  // Sender selection state - default to shared email (personal is exception)
  const [senderEmail, setSenderEmail] = useState(DEFAULT_SENDER_EMAIL);
  const [displayName, setDisplayName] = useState(DEFAULT_DISPLAY_NAME);

  // Smart default: update display name when sender email changes
  const handleSenderEmailChange = useCallback(
    (email: string) => {
      setSenderEmail(email);

      // Auto-update display name based on selection
      if (email === agentEmail) {
        setDisplayName(agentName || '');
      } else {
        const sharedEmail = SHARED_SENDER_EMAILS.find((e) => e.email === email);
        if (sharedEmail) {
          setDisplayName(sharedEmail.defaultDisplayName);
        }
      }
    },
    [agentEmail, agentName],
  );

  // Additional recipients state
  const [recipientFields, setRecipientFields] = useState({
    secondaryTo: { show: false, value: '' },
    cc: { show: false, value: '' },
    bcc: { show: false, value: '' },
  });

  const [draftAttachments, setDraftAttachments] = useState<SerializableAttachment[]>([]);

  const { sendEmail, isSubmitting, error } = useCreateEmail();

  const onAttachmentsAdded = useCallback((newAttachments: Attachment[]) => {
    setAttachments((prev) => [...prev, ...newAttachments]);
  }, []);

  const { handleFilePaste } = useFileUploadPaste({
    attachmentsCount: attachments.length,
    maxAttachments: MAX_ATTACHMENTS,
    onAttachmentsAdded,
  });

  const getCurrentDraft = useCallback(
    (): Omit<EmailDraft, 'updatedAt'> => ({
      subject,
      body,
      attachments: draftAttachments,
      senderEmail,
      displayName,
      recipientFields,
    }),
    [subject, body, draftAttachments, senderEmail, displayName, recipientFields],
  );

  const handleDraftLoaded = useCallback(
    (draft: EmailDraft) => {
      // Only load subject from draft if no initialSubject is provided
      if (!initialSubject) {
        setSubject(draft.subject);
      }
      setBody(draft.body);
      setAttachments(deserializeAttachments(draft.attachments));
      setDraftAttachments(draft.attachments);
      setSenderEmail(draft.senderEmail);
      setDisplayName(draft.displayName);
      setRecipientFields(draft.recipientFields);
    },
    [initialSubject],
  );

  const isDraftEmpty = useCallback(
    (draft: Omit<EmailDraft, 'updatedAt'>) =>
      !draft.subject.trim() && !draft.body.trim() && draft.attachments.length === 0,
    [],
  );

  const { saveDraft, clearDraft } = useComposeDraft<EmailDraft>({
    type: 'email',
    customerId,
    getCurrentDraft,
    onDraftLoaded: handleDraftLoaded,
    isDraftEmpty,
    skipInitialLoad: !!initialSubject,
  });

  // Serialize attachments when they change
  useEffect(() => {
    serializeAttachments(attachments).then(setDraftAttachments);
  }, [attachments]);

  // Sync subject when initialSubject changes (e.g., clicking Reply on a message)
  useEffect(() => {
    if (initialSubject) {
      setSubject(initialSubject);
    }
  }, [initialSubject]);

  // Save draft when fields change
  useEffect(() => {
    saveDraft();
  }, [subject, body, draftAttachments, senderEmail, displayName, recipientFields, saveDraft]);

  const hasContent = htmlToPlainText(body).trim() || attachments.length > 0;
  const isDisabled = isSubmitting || !customerEmail || !subject.trim() || !hasContent;

  type RecipientFieldKey = 'secondaryTo' | 'cc' | 'bcc';

  const updateRecipientField = (field: RecipientFieldKey, updates: Partial<{show: boolean; value: string}>) => {
    setRecipientFields((prev) => ({
      ...prev,
      [field]: { ...prev[field], ...updates },
    }));
  };

  const handleCloseRecipientField = (field: RecipientFieldKey) => {
    updateRecipientField(field, { show: false, value: '' });
  };

  const resetForm = useCallback(() => {
    setSubject('');
    setBody('');
    setAttachments([]);
    setRecipientFields({
      secondaryTo: { show: false, value: '' },
      cc: { show: false, value: '' },
      bcc: { show: false, value: '' },
    });
    setSenderEmail(DEFAULT_SENDER_EMAIL);
    setDisplayName(DEFAULT_DISPLAY_NAME);
    clearDraft();
  }, [clearDraft]);

  const handleSubmit = useCallback(async() => {
    if (!customerId || !customerEmail || !subject.trim() || (!htmlToPlainText(body).trim() && attachments.length === 0)) {
      return;
    }

    // Convert HTML to Markdown before creating optimistic entry so it renders correctly
    const markdownBody = htmlToMarkdown(body).trim();
    const emailHtml = body.trim();

    // Create optimistic message with markdown (EmailMessage expects markdown for outbound)
    let optimisticEntry: UnifiedDialogEntry | null = null;
    if (onAddOptimisticMessage) {
      optimisticEntry = createOptimisticEmail({
        subject: subject.trim(),
        body: markdownBody,
        toEmail: customerEmail,
        fromEmail: senderEmail,
        fromName: displayName,
      });
      onAddOptimisticMessage(optimisticEntry);
    }

    const secondaryToAddresses = parseEmailsFromString(recipientFields.secondaryTo.value);
    const ccAddresses = parseEmailsFromString(recipientFields.cc.value);
    const bccAddresses = parseEmailsFromString(recipientFields.bcc.value);

    const success = await sendEmail({
      customerId,
      toEmailAddress: customerEmail,
      toName: customerName,
      subject: subject.trim(),
      body: markdownBody,
      htmlBody: emailHtml || undefined,
      fromEmailAddress: senderEmail,
      fromName: displayName,
      secondaryToAddresses: secondaryToAddresses.length > 0 ? secondaryToAddresses : undefined,
      ccAddresses: ccAddresses.length > 0 ? ccAddresses : undefined,
      bccAddresses: bccAddresses.length > 0 ? bccAddresses : undefined,
      attachments,
    });

    if (success) {
      resetForm();
      // Call onSuccess first (triggers refetch)
      await onSuccess?.();
      // Remove optimistic message AFTER API data is loaded
      if (optimisticEntry && onRemoveOptimisticMessage) {
        onRemoveOptimisticMessage(optimisticEntry.optimisticId!);
      }
    } else if (optimisticEntry && onRemoveOptimisticMessage) {
      // Remove optimistic message on failure
      onRemoveOptimisticMessage(optimisticEntry.optimisticId!);
    }
  }, [
    customerId,
    customerEmail,
    customerName,
    subject,
    body,
    senderEmail,
    displayName,
    recipientFields,
    attachments,
    onSuccess,
    sendEmail,
    resetForm,
    onAddOptimisticMessage,
    onRemoveOptimisticMessage,
  ]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Cmd + Enter (Mac) or Ctrl + Enter (Windows/Linux) to send
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  if (!customerEmail) {
    return (
      <div className="flex flex-col gap-3 w-full shrink-0">
        <Notification mode="embed" type="Warning">
          No email address found for this customer.
        </Notification>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 w-full shrink-0">
      {customerName && (
        <Notification mode="embed" size="small" type="Information">
          You are about to send an email to {customerName} ({customerEmail}).
        </Notification>
      )}

      {error && (
        <Notification mode="embed" type="Error">
          {error}
        </Notification>
      )}

      {/* Sender Selection Row */}
      <SenderSelector
        disabled={isSubmitting}
        displayName={displayName}
        onDisplayNameChange={setDisplayName}
        onSenderEmailChange={handleSenderEmailChange}
        personalEmail={agentEmail || ''}
        personalName={agentName || ''}
        senderEmail={senderEmail} />

      {/* To field with recipient toggles */}
      <div className="flex items-center gap-2">
        <div className="flex flex-1 items-center gap-2 text-gray-600 dark:text-gray-400 text-sm">
          <Label className="shrink-0">To:</Label> {customerEmail}
        </div>
        <div className="flex gap-1">
          {!recipientFields.secondaryTo.show && (
            <Button
              data-testid="add-recipient-button"
              flavor="regular"
              onClick={() => updateRecipientField('secondaryTo', { show: true })}
              size="small"
              taste="boundless">
              <UserPlusIcon className="size-4" />
            </Button>
          )}
          {!recipientFields.cc.show && (
            <Button
              flavor="regular"
              onClick={() => updateRecipientField('cc', { show: true })}
              size="small"
              taste="boundless">
              Cc
            </Button>
          )}
          {!recipientFields.bcc.show && (
            <Button
              flavor="regular"
              onClick={() => updateRecipientField('bcc', { show: true })}
              size="small"
              taste="boundless">
              Bcc
            </Button>
          )}
        </div>
      </div>

      {/* Secondary To field */}
      {recipientFields.secondaryTo.show && (
        <EmailAutocompleteInput
          disabled={isSubmitting}
          label="More recipients"
          onChange={(value) => updateRecipientField('secondaryTo', { value })}
          onClose={() => handleCloseRecipientField('secondaryTo')}
          value={recipientFields.secondaryTo.value} />
      )}

      {/* CC field */}
      {recipientFields.cc.show && (
        <EmailAutocompleteInput
          disabled={isSubmitting}
          label="Cc"
          onChange={(value) => updateRecipientField('cc', { value })}
          onClose={() => handleCloseRecipientField('cc')}
          value={recipientFields.cc.value} />
      )}

      {/* BCC field */}
      {recipientFields.bcc.show && (
        <EmailAutocompleteInput
          disabled={isSubmitting}
          label="Bcc"
          onChange={(value) => updateRecipientField('bcc', { value })}
          onClose={() => handleCloseRecipientField('bcc')}
          value={recipientFields.bcc.value} />
      )}

      <Input
        className="flex-1"
        disabled={isSubmitting}
        label={LABEL_SUBJECT}
        onChange={(_event, _name, value) => setSubject(value || '')}
        placeholder={PLACEHOLDER_SUBJECT}
        value={subject} />

      <RichTextInput
        className="flex-1"
        isDisabled={isSubmitting}
        label={LABEL_MESSAGE}
        minRows={4}
        onChange={setBody}
        onFilePaste={handleFilePaste}
        onKeyDown={handleKeyDown}
        placeholder={PLACEHOLDER_MESSAGE}
        value={body} />

      <div className="flex lg:flex-row flex-col gap-4">
        <UploadArea filesList={attachments} maxFileSize={MAX_ATTACHMENT_SIZE} maxUploads={MAX_ATTACHMENTS} onChange={setAttachments} />
        <Button
          className="lg:max-w-36" disabled={isDisabled} flavor="primary" isFullWidth
          onClick={handleSubmit}>
          {isSubmitting ? (
            <>
              <Spinner active color="white" size={18} />
              {BUTTON_LOADING_TEXT}
            </>
          ) : (
            <>
              <EnvelopeOpenIcon className="size-5 shrink-0" />
              {BUTTON_TEXT}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EmailInput;
